import { Fragment, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
    ArrowLeft,
    CreditCard,
    GitBranch,
    History,
    Pencil,
    QrCode,
    ShoppingCart,
    Users,
} from 'lucide-react'

import type { PackagingDraftItem } from '@/lib/api/sales-order-api'
import type { OrdersSearch } from './index'

import { OrderLotTraceDialog } from '@/components/orders/order-lot-trace-dialog'
import { FormSection } from '@/components/forms/form-section'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { PaymentQRDialog } from '@/components/payment-qr-dialog'
import { OrderStatusBadge } from '@/components/sections/order-status-badge'
import { OrderSLAPanel } from '@/components/sections/order-sla-panel'
import { OrderTimeline } from '@/components/sections/order-timeline'
import { OrderActions } from '@/components/sections/order-actions'
import { OrderPXKCard } from '@/components/sections/order-pxk-card'
import { OrderPackagingDeducted } from '@/components/orders/order-packaging-deducted'
import { AwaitingPackagingPanel } from '@/components/orders/awaiting-packaging-panel'
import { OrderMaterialDetailRow } from '@/components/orders/order-material-detail-row'
import { EInvoicePanel } from '@/components/sections/einvoice-panel'
import { CarrierTrackingPanel } from '@/components/carrier/carrier-tracking-panel'
import { InvoiceDownloadActions } from '@/components/sections/invoice-download-actions'
import { OrderDeliveryCameraCard } from '@/components/orders/order-delivery-camera-card'
import { PaymentForm } from '@/components/forms/payment-form'
import { EditOrderForm } from '@/components/forms/edit-order-form'
import { useAuth } from '@/hooks/use-auth'
import {
    useOrderPackagingDraft,
    useOrderPayments,
    useOrderTimeline,
    useRecordOrderPayments,
    useSalesOrder,
} from '@/hooks/use-sales-order-query'
import { useProductLotOutNotesQuery } from '@/hooks/use-product-lot-query'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import { useCarrierShipment } from '@/hooks/use-carrier-shipment-query'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { formatVnd } from '@/lib/format'
import { LIST_SEARCH_KEYS, recallListSearch } from '@/lib/list-search-store'
import { canEditOrder, canEditOrderItems } from '@/lib/order-edit'
import { InternalOrderCodeRow } from '@/components/orders/internal-order-code-row'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/orders/$orderId')({
    component: OrderDetailPage,
})

const PAYMENT_LABELS: Record<string, string> = {
    unpaid: m.order_payment_unpaid(),
    partial: m.order_payment_partial(),
    paid: m.order_payment_paid(),
}

function OrderDetailPage() {
    const { orderId } = Route.useParams()
    const [payOpen, setPayOpen] = useState(false)
    const [qrOpen, setQrOpen] = useState(false)
    const [editing, setEditing] = useState(false)
    const [traceOpen, setTraceOpen] = useState(false)
    const { hasPermission } = useAuth()

    const { data: order, isLoading } = useSalesOrder(orderId)
    const { data: linkedPxkList } = useProductLotOutNotesQuery(
        { sales_order_id: orderId, per_page: 1 },
        { enabled: !!orderId },
    )
    const linkedPxk = linkedPxkList?.data?.[0] ?? null
    const { data: timeline = [], isLoading: timelineLoading } =
        useOrderTimeline(orderId)
    const { data: payments = [] } = useOrderPayments(orderId)
    const { data: invoiceSettings } = useInvoiceSettings()
    // A parcel booked with a shipping carrier leaves our hands at dispatch: the
    // driver controls no longer apply to it, and its progress comes from the
    // carrier's pushes instead.
    const carrierEnabled = Boolean(useFeatureFlag('dev-carrier-shipping'))
    const { data: carrierShipment } = useCarrierShipment(
        orderId,
        carrierEnabled,
    )
    const { data: packagingDraft = [] } = useOrderPackagingDraft(orderId)
    const recordPayments = useRecordOrderPayments(orderId)

    if (isLoading) {
        return (
            <div className="text-muted-foreground text-sm">
                {m.common_loading()}
            </div>
        )
    }
    if (!order) {
        return (
            <div className="text-muted-foreground text-sm">
                {m.order_detail_not_found()}
            </div>
        )
    }

    const items = order.items ?? []
    // Vật tư thuộc định mức của DÒNG SP (order_item_id set) — render thành sub-row
    // dưới đúng sản phẩm, đồng nhất với màn Tạo/Sửa. Vật tư thêm tay/order-level
    // (order_item_id null, KHÔNG thuộc định mức SP) KHÔNG hiện trong bảng SP.
    // Nguồn = draft đã lưu (KHÔNG recompute BOM) để đơn cũ không sai khi sau này
    // đổi công thức/giá vật tư. Giá đã là snapshot (0 nếu gộp vào giá SP).
    const materialsByItem = new Map<string, PackagingDraftItem[]>()
    for (const d of packagingDraft) {
        if (d.quantity > 0 && d.order_item_id) {
            const list = materialsByItem.get(d.order_item_id) ?? []
            list.push(d)
            materialsByItem.set(d.order_item_id, list)
        }
    }
    // Vật tư KHÔNG gắn dòng đơn → dòng ngang hàng sản phẩm.
    //
    // Lọc theo order_item_id chứ KHÔNG theo source: hai bộ lọc phải phủ kín và
    // không chồng nhau. Lọc cũ dùng source='manual' để lại khoảng trống — dòng
    // bom_autofill mà order_item_id NULL (DB thật đang có 15 dòng) không vào
    // nhóm nào nên biến mất khỏi bảng, trong khi tổng đơn lấy từ server vẫn
    // tính tiền chúng. Nó cũng chồng lấn ở chiều ngược lại: dòng source=manual
    // có order_item_id sẽ hiện hai lần trong cùng một bảng.
    const manualMaterials = packagingDraft.filter(
        (d) => !d.order_item_id && d.quantity > 0,
    )
    // Round cả 2 để tránh legacy orders chưa migrate hiển thị âm 0.xx.
    const remaining =
        Math.round(order.total_amount) - Math.round(order.paid_amount)
    // Đơn đã hủy: ẩn mọi nút thao tác thanh toán (QR, ghi nhận, đánh dấu đã trả)
    // vì không còn nghĩa vụ thu tiền trên đơn hủy.
    const isCancelled = order.status === 'cancelled'
    // Derive VAT breakdown from total (entity stores only the post-VAT total).
    // Ưu tiên VAT snapshot của đơn (người bán chỉnh riêng); đơn cũ fallback global.
    // Legacy orders có total_amount decimal → Math.round để VAT hiển thị integer.
    const vatPercent = order.vat_percent ?? invoiceSettings?.vat_percent ?? 0
    const vatAmount =
        order.vat_amount != null
            ? Math.round(order.vat_amount)
            : vatPercent > 0
              ? Math.round(
                    order.total_amount -
                        Math.round(order.total_amount / (1 + vatPercent / 100)),
                )
              : 0
    const subtotalPreVat = Math.round(order.total_amount) - vatAmount

    return (
        <div className="space-y-4 p-4 md:space-y-6 md:p-6">
            {/* Header */}
            <div className="flex flex-wrap items-start gap-3">
                <Button asChild variant="ghost" size="icon">
                    <Link
                        to="/orders"
                        search={
                            recallListSearch<OrdersSearch>(
                                LIST_SEARCH_KEYS.orders,
                            ) ?? {}
                        }
                    >
                        <ArrowLeft className="size-4" />
                    </Link>
                </Button>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-xl font-semibold font-mono">
                            {order.order_code}
                        </h1>
                        <OrderStatusBadge status={order.status} />
                        <Badge
                            variant={
                                order.payment_status === 'paid'
                                    ? 'success'
                                    : order.payment_status === 'partial'
                                      ? 'warning'
                                      : 'outline'
                            }
                        >
                            {PAYMENT_LABELS[order.payment_status]}
                        </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        {new Date(order.created_at).toLocaleString('vi-VN')}
                    </p>
                </div>
                {canEditOrder(order.status, order.payment_status) &&
                    hasPermission('sales_orders.update') &&
                    !editing && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditing(true)}
                        >
                            <Pencil className="mr-1 size-4" />{' '}
                            {m.order_edit_btn()}
                        </Button>
                    )}
                <InvoiceDownloadActions
                    orderId={orderId}
                    order={order}
                    companyName={invoiceSettings?.company_name}
                    companyPhone={invoiceSettings?.company_phone}
                />
            </div>

            {/* SLA info */}
            <OrderSLAPanel
                deadlineAt={order.sla_deadline_at}
                breached={order.sla_breached}
                warningAt={order.sla_warning_at}
            />

            {/* Workflow actions */}
            {!editing && (
                <OrderActions
                    orderId={orderId}
                    status={order.status}
                    onActionComplete={() => {}}
                    viaCarrier={Boolean(carrierShipment)}
                />
            )}

            {/* Vận chuyển qua hãng — chỉ hiện khi đơn có vận đơn đang chạy. */}
            {!editing && <CarrierTrackingPanel orderId={orderId} />}

            {/* Đơn chờ đóng gói — panel kiểm tra tồn để cashier biết khi nào Confirm được. */}
            {!editing && order.status === 'awaiting_packaging' && (
                <AwaitingPackagingPanel order={order} items={items} />
            )}

            {/* PXK linked — hiển thị cho thủ kho click vào detail lấy hàng */}
            {!editing && <OrderPXKCard pxk={linkedPxk} />}

            {/* Truy xuất nguồn gốc — chỉ khi đơn có PXK approved (có lô để trace). */}
            {!editing && linkedPxk?.status === 'approved' && (
                <div className="flex justify-end">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setTraceOpen(true)}
                    >
                        <GitBranch className="mr-1 size-4" />
                        {m.order_lot_trace_button()}
                    </Button>
                </div>
            )}
            <OrderLotTraceDialog
                open={traceOpen}
                orderId={orderId}
                onOpenChange={setTraceOpen}
            />

            {/* eInvoice — only after delivery is confirmed. Showing the panel
                earlier triggered a repeating 404 because the einvoice row
                doesn't exist until payment/delivery completes. */}
            {!editing && order.status === 'delivered' && (
                <EInvoicePanel orderId={orderId} />
            )}

            {/* Edit form */}
            {editing && (
                <div className="space-y-4">
                    <div className="flex items-center gap-2">
                        <Pencil className="size-5 text-primary" />
                        <h2 className="text-lg font-semibold tracking-tight">
                            {m.order_edit_title()}
                        </h2>
                    </div>
                    <EditOrderForm
                        order={order}
                        itemsEditable={canEditOrderItems(order.status)}
                        onSuccess={() => {
                            setEditing(false)
                        }}
                        onCancel={() => setEditing(false)}
                    />
                </div>
            )}

            {/* Main content grid */}
            {!editing && (
                <div className="grid gap-5 lg:grid-cols-[minmax(320px,380px)_minmax(0,1fr)] lg:items-start">
                    {/* Right column — products + payment (wide) */}
                    <div className="space-y-5 lg:col-start-2 lg:row-start-1">
                        {/* Items */}
                        <FormSection
                            icon={<ShoppingCart />}
                            title={m.order_products()}
                        >
                            <div className="overflow-x-auto rounded-lg border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>
                                                {m.order_products()}
                                            </TableHead>
                                            <TableHead className="text-right">
                                                SL
                                            </TableHead>
                                            <TableHead className="text-right">
                                                {m.order_unit_price()}
                                            </TableHead>
                                            <TableHead className="text-right">
                                                CK
                                            </TableHead>
                                            <TableHead className="text-right">
                                                {m.order_subtotal()}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {items.map((item) => (
                                            <Fragment key={item.id}>
                                                <TableRow>
                                                    <TableCell>
                                                        {item.product_name}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {item.quantity}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {formatVnd(
                                                            item.unit_price,
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {item.discount_percent}%
                                                    </TableCell>
                                                    <TableCell className="text-right font-medium">
                                                        {formatVnd(
                                                            item.subtotal,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                {(
                                                    materialsByItem.get(
                                                        item.id,
                                                    ) ?? []
                                                ).map((d) => (
                                                    <OrderMaterialDetailRow
                                                        key={d.id}
                                                        d={d}
                                                        variant="sub"
                                                    />
                                                ))}
                                            </Fragment>
                                        ))}
                                        {/* Vật tư nhân viên tự thêm — ngang
                                            hàng sản phẩm, đồng nhất với màn
                                            tạo/sửa. */}
                                        {manualMaterials.map((d) => (
                                            <OrderMaterialDetailRow
                                                key={d.id}
                                                d={d}
                                                variant="standalone"
                                            />
                                        ))}
                                        {vatPercent > 0 && (
                                            <>
                                                <TableRow className="bg-muted/30">
                                                    <TableCell
                                                        colSpan={4}
                                                        className="text-right text-muted-foreground"
                                                    >
                                                        {m.order_items_subtotal()}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {formatVnd(
                                                            subtotalPreVat,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow className="bg-muted/30">
                                                    <TableCell
                                                        colSpan={4}
                                                        className="text-right text-muted-foreground"
                                                    >
                                                        {m.order_vat_label({
                                                            percent:
                                                                String(
                                                                    vatPercent,
                                                                ),
                                                        })}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {formatVnd(vatAmount)}
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        )}
                                        <TableRow className="bg-muted/30 font-bold">
                                            <TableCell
                                                colSpan={4}
                                                className="text-right"
                                            >
                                                {m.order_grand_total()}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {formatVnd(
                                                    Math.round(
                                                        order.total_amount,
                                                    ),
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        {(() => {
                                            const totalWeight = items.reduce(
                                                (sum, item) =>
                                                    sum +
                                                    (item.weight_value ?? 0) *
                                                        item.quantity,
                                                0,
                                            )
                                            if (totalWeight <= 0) return null
                                            const unit =
                                                items.find((i) => i.weight_unit)
                                                    ?.weight_unit ?? 'g'
                                            return (
                                                <TableRow className="text-muted-foreground text-xs">
                                                    <TableCell
                                                        colSpan={4}
                                                        className="text-right"
                                                    >
                                                        {m.order_total_weight()}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {totalWeight >= 1000
                                                            ? `${(totalWeight / 1000).toFixed(1)} kg`
                                                            : `${totalWeight} ${unit}`}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })()}
                                    </TableBody>
                                </Table>
                            </div>
                        </FormSection>

                        {/* Payments */}
                        <FormSection
                            icon={<CreditCard />}
                            title={m.order_payment_title()}
                            action={
                                remaining > 0 && !isCancelled ? (
                                    <div className="flex gap-2">
                                        {/* QR view only needs read; recording a
                                                payment needs customers.payment. */}
                                        {hasPermission('sales_orders.read') && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setQrOpen(true)}
                                            >
                                                <QrCode className="mr-1 size-4" />
                                                {m.payment_qr_open_button()}
                                            </Button>
                                        )}
                                        {hasPermission('customers.payment') && (
                                            <Button
                                                size="sm"
                                                onClick={() => setPayOpen(true)}
                                            >
                                                {m.order_record_payment()}
                                            </Button>
                                        )}
                                        {/* One-click: record the full
                                                remaining as paid. For cash /
                                                off-account transfer / no
                                                matching content — the manual
                                                source of truth, independent of
                                                the SePay webhook. */}
                                        {hasPermission('customers.payment') && (
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                disabled={
                                                    recordPayments.isPending
                                                }
                                                onClick={() =>
                                                    void recordPayments.mutateAsync(
                                                        [
                                                            {
                                                                amount: remaining,
                                                                method: 'cash',
                                                                reference:
                                                                    m.order_mark_paid_reference(),
                                                            },
                                                        ],
                                                    )
                                                }
                                            >
                                                {m.order_mark_paid_button()}
                                            </Button>
                                        )}
                                    </div>
                                ) : null
                            }
                        >
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">
                                        {m.order_paid()}
                                    </span>
                                    <span className="font-medium">
                                        {formatVnd(
                                            Math.round(order.paid_amount),
                                        )}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">
                                        {m.order_remaining()}
                                    </span>
                                    <span
                                        className={
                                            remaining > 0
                                                ? 'font-bold text-destructive'
                                                : 'font-medium'
                                        }
                                    >
                                        {formatVnd(remaining)}
                                    </span>
                                </div>
                                {remaining > 0 &&
                                    order.settlement_intent ===
                                        'bank_transfer' && (
                                        <p className="text-xs font-medium text-amber-600">
                                            {m.order_settlement_intent_bank()}
                                        </p>
                                    )}
                                {remaining > 0 &&
                                    order.settlement_intent === 'credit' && (
                                        <p className="text-xs text-muted-foreground">
                                            {m.order_settlement_intent_credit()}
                                        </p>
                                    )}
                                {payments.length > 0 && (
                                    <div className="mt-3 space-y-2 border-t pt-3">
                                        {payments.map((p) => (
                                            <div
                                                key={p.id}
                                                className="space-y-0.5 text-xs text-muted-foreground"
                                            >
                                                <div className="flex justify-between">
                                                    <span>
                                                        {p.payment_method ===
                                                        'bank_transfer'
                                                            ? m.payment_method_bank()
                                                            : p.payment_method ===
                                                                'card'
                                                              ? m.payment_method_card()
                                                              : m.payment_method_cash()}{' '}
                                                        —{' '}
                                                        {new Date(
                                                            p.created_at,
                                                        ).toLocaleString(
                                                            'vi-VN',
                                                        )}
                                                    </span>
                                                    <span className="font-medium">
                                                        {formatVnd(p.amount)}
                                                    </span>
                                                </div>
                                                <div className="flex gap-3 text-muted-foreground/70">
                                                    {p.collected_by_driver_name ? (
                                                        <span>
                                                            {m.order_collected_by_driver()}
                                                            :{' '}
                                                            {
                                                                p.collected_by_driver_name
                                                            }
                                                        </span>
                                                    ) : (
                                                        p.created_by_name && (
                                                            <span>
                                                                {m.order_seller()}
                                                                :{' '}
                                                                {
                                                                    p.created_by_name
                                                                }
                                                            </span>
                                                        )
                                                    )}
                                                    {p.reference && (
                                                        <span>
                                                            {m.order_transfer_code()}
                                                            : {p.reference}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </FormSection>
                    </div>

                    {/* Left column — customer + timeline + auxiliary panels */}
                    <div className="space-y-5 lg:col-start-1 lg:row-start-1">
                        <FormSection
                            icon={<Users />}
                            title={m.order_customer_info()}
                        >
                            <div className="space-y-1.5 text-sm">
                                <Row
                                    label={m.customer_name()}
                                    value={order.customer_name}
                                />
                                <Row
                                    label={m.customer_phone()}
                                    value={order.customer_phone}
                                />
                                <Row
                                    label={m.order_shipping_address()}
                                    value={order.shipping_address}
                                />
                                {order.shipping_ward && (
                                    <Row
                                        label={m.customer_ward_label()}
                                        value={order.shipping_ward}
                                    />
                                )}
                                {order.shipping_province && (
                                    <Row
                                        label={m.customer_province_label()}
                                        value={order.shipping_province}
                                    />
                                )}
                                {order.shipping_carrier_name && (
                                    <Row
                                        label={m.shipping_carrier()}
                                        value={order.shipping_carrier_name}
                                    />
                                )}
                                <Row
                                    label={m.order_channel_label()}
                                    value={order.source_channel}
                                />
                                <InternalOrderCodeRow
                                    orderId={order.id}
                                    createdById={order.created_by_id}
                                    internalOrderCode={
                                        order.internal_order_code
                                    }
                                />
                                {order.product_notes && (
                                    <Row
                                        label={m.order_product_notes_label()}
                                        value={order.product_notes}
                                    />
                                )}
                            </div>
                        </FormSection>
                        <OrderDeliveryCameraCard order={order} />
                        <OrderPackagingDeducted orderId={orderId} />
                        <FormSection
                            icon={<History />}
                            title={m.order_status_history()}
                        >
                            <OrderTimeline
                                logs={timeline}
                                isLoading={timelineLoading}
                            />
                        </FormSection>
                    </div>
                </div>
            )}

            {/* QR Payment dialog */}
            <PaymentQRDialog
                open={qrOpen}
                onOpenChange={setQrOpen}
                orderId={orderId}
            />

            {/* Payment dialog */}
            <Dialog open={payOpen} onOpenChange={setPayOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{m.order_record_payment()}</DialogTitle>
                    </DialogHeader>
                    <PaymentForm
                        defaultAmount={remaining > 0 ? remaining : 0}
                        onSubmit={async (data) => {
                            await recordPayments.mutateAsync([data])
                            setPayOpen(false)
                        }}
                        onCancel={() => setPayOpen(false)}
                        isPending={recordPayments.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex justify-between gap-4">
            <span className="text-muted-foreground shrink-0">{label}</span>
            <span className="text-right">{value}</span>
        </div>
    )
}
